/** The `<page-context>` channel: the page a Kory message was sent from. */

import { pageSharing } from "@/lib/kory-page-sharing";
import { pageHeading, routeArea, screenDigest } from "@/lib/kory-screen-digest";
import { sectionSummaries } from "@/lib/kory-section-summaries";

// Max bytes of page text we'll quietly attach to a user turn. A few big
// scorecard tables run ~30-50KB of text; 40KB keeps us inside token budget
// without losing most real-world pages.
const PAGE_CONTEXT_MAX_BYTES = 40_000;

export interface PageContext {
  path: string;
  title: string;
}

interface CapturedPageContext {
  text: string; // the <page-context>…</page-context> block to prepend
  meta: PageContext;
}

/** What someone types never goes to Kory: form fields, rich-text editors, and regions a page marks private. */
const FIELDS = 'input, textarea, select, [contenteditable]:not([contenteditable="false"]), [data-kory-private]';

const UNRENDERED = "style, script, template, noscript";

/** The rendered text of `root`, minus every field in it. Subtrees without a field
 *  keep `innerText` (rendered, hidden content skipped); only the path to a field is walked. */
export function visibleText(root: HTMLElement): string {
  if (!root.querySelector(FIELDS)) return root.innerText ?? root.textContent ?? "";
  const parts: string[] = [];
  for (const node of root.childNodes) {
    if (node.nodeType === Node.TEXT_NODE) parts.push(node.textContent ?? "");
    else if (node instanceof HTMLElement && !node.matches(`${FIELDS}, ${UNRENDERED}`)) {
      // A hidden element's innerText is its whole textContent; skip it (another tab, a closed panel).
      if (typeof node.checkVisibility === "function" && !node.checkVisibility()) continue;
      parts.push(`\n${visibleText(node)}\n`);
    }
  }
  return parts.join("").replace(/\n{3,}/g, "\n\n");
}

/** The page the user is on: its path, its heading, a digest of the screen, what each Overview section
 *  shows, and its text (capped), with a `note` leading the text. The This page chip's choice for the
 *  route (`pageSharing`) decides how much: the sections and text only when the page is shared, the
 *  heading and digest unless nothing is. */
export function capturePage(note?: string | null): (PageContext & { text: string; truncated: boolean }) | null {
  // Skip when Kory was invoked from its dedicated page — there's no
  // surrounding context to reason about there.
  if (typeof window === "undefined") return null;
  if (window.location.pathname === "/kory") return null;

  const main = document.querySelector("main") ?? document.body;
  const sharing = pageSharing(window.location.pathname);
  const text = sharing === "page" ? visibleText(main).trim() : "";
  const digest = screenDigest({ koryOnly: sharing === "none" });
  const sections = sharing === "page" ? sectionSummaries() : null;

  // The note, digest and sections lead the page text, so truncation never cuts them; the text gets the rest.
  const lead = [note, digest, sections].filter(Boolean).join("\n\n");
  const room = Math.max(0, PAGE_CONTEXT_MAX_BYTES - lead.length);
  const truncated = text.length > room;
  const pageText = truncated ? text.slice(0, room) : text;
  const body = [lead, pageText].filter(Boolean).join("\n\n");
  if (!body) return null;
  // document.title is the shared app title ("Hickory AI") — not useful as a
  // page label. Prefer the page's <h1>; fall back to document.title.
  // An unshared page's heading and anything past its app area can carry its data (payroll, commissions, HR), so they stay out.
  const unshared = sharing === "none";
  const heading = unshared ? "" : pageHeading();
  const title = (heading || document.title || "").slice(0, 80);
  const pathname = unshared ? routeArea(window.location.pathname) : window.location.pathname + window.location.search;
  return { path: pathname, title, text: body, truncated };
}

/** The page the user is on as a `<page-context>` block (every Kory renderer strips it).
 *  A `note` leads the body, so the model reads it before the page text. */
export function capturePageContext(note?: string | null): CapturedPageContext | null {
  const page = capturePage(note);
  if (!page) return null;
  const block = [
    `<page-context path="${page.path}" title=${JSON.stringify(page.title)}${page.truncated ? ' truncated="true"' : ""}>`,
    page.text,
    "</page-context>",
  ].join("\n");

  return { text: block, meta: { path: page.path, title: page.title } };
}
